/**
 * Plan Diff Utility
 * Pure functions to compare two plan form data objects and summarize changes.
 */

import type { StepChangeSummary } from '../types/alternativePlan';
import { formatCurrency } from './formatters';
import { deriveTotalPriorOtherIncome } from './priorIncomeSeed';

/**
 * Step-to-field mapping. Each step "owns" certain top-level form data keys.
 */
const STEP_FIELD_MAP: Record<number, { label: string; fields: string[] }> = {
  1: { label: 'Plan Basics', fields: ['planName', 'planType', 'planAsOfDate'] },
  2: { label: 'People', fields: ['persons'] },
  3: { label: 'Investments', fields: ['holdings', 'additionalPensions', 'contributions'] },
  4: { label: 'Property', fields: ['includeProperty', 'property', 'properties'] },
  5: { label: 'Income', fields: ['supplementaryIncome'] },
  6: { label: 'Spending', fields: ['spendingPhases', 'oneTimeExpenses', 'recurringExpenses'] },
  7: { label: 'Later Life & Estate', fields: ['personDeceasedAges', 'survivorSpendingPct', 'careScenarios', 'hasDirectDescendants', 'includePensionsInEstate', 'ihtStrategies', 'careFund', 'careFundReleaseAge'] },
  8: { label: 'Assumptions', fields: ['householdCash', 'economicAssumptions'] },
  9: { label: 'Strategy', fields: ['decumulationStrategy', 'projectionYears', 'isaAnnualAllowance', 'isaShelteringMode', 'bandFillMode', 'guardrailConfig', 'emergencyFund', 'annuityPurchases'] },
  10: { label: 'Projection Settings', fields: ['timeHorizonRebalancing', 'spendingApproachConfig'] },
};

/**
 * Get the field keys owned by a given step number.
 */
export function getStepFields(stepNumber: number): string[] {
  return STEP_FIELD_MAP[stepNumber]?.fields ?? [];
}

/** True for null/undefined and for NaN — react-hook-form's `valueAsNumber` leaves a
 * cleared numeric field (e.g. spending phase "End Year") as NaN rather than undefined,
 * so an "unset" value can be represented either way depending on whether it just came
 * from live form state or from a server round-trip (JSON serializes NaN to null). Both
 * must count as "no value" here, or a freshly-cleared field reads as still-changed
 * until the next full reload silently normalizes it. */
/**
 * Sum one numeric field across an array of unknown-shaped plan items.
 *
 * The diff walks raw saved form data, so the elements really are unknown —
 * this reads the field defensively in one place rather than asserting an
 * element type at each call site.
 */
function sumNumericField(items: unknown[], field: string): number {
  return items.reduce<number>((sum, item) => {
    if (typeof item !== 'object' || item === null) return sum;
    return sum + (Number((item as Record<string, unknown>)[field]) || 0);
  }, 0);
}

function isEmptyValue(v: unknown): boolean {
  return v == null || (typeof v === 'number' && Number.isNaN(v));
}

/**
 * Deep equality check for two values.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  if (aEmpty && bEmpty) return true;
  if (aEmpty || bEmpty) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, idx) => deepEqual(item, b[idx]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(aObj), ...Object.keys(bObj)]);
    return [...allKeys].every(key => deepEqual(aObj[key], bObj[key]));
  }

  return false;
}

/**
 * Scalar inequality check for the human-readable describers below — NaN-safe
 * counterpart to `!==`. These only run on a step already known (via
 * `computeStepChanges`/`deepEqual`) to have *some* changed field, but without
 * this a cleared numeric field elsewhere in the same step (NaN vs NaN, or NaN
 * vs undefined) can still produce a spurious "before → after" line describing
 * a field that didn't actually change.
 */
function fieldsDiffer(a: unknown, b: unknown): boolean {
  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  if (aEmpty && bEmpty) return false;
  if (aEmpty || bEmpty) return true;
  return a !== b;
}

/**
 * Compute step-by-step change summary between primary and alternative form data.
 */
export function computeStepChanges(
  primary: Record<string, unknown>,
  alternative: Record<string, unknown>
): StepChangeSummary[] {
  const summaries: StepChangeSummary[] = [];

  for (const [stepStr, config] of Object.entries(STEP_FIELD_MAP)) {
    const stepNumber = Number(stepStr);
    const changedFields: string[] = [];

    for (const field of config.fields) {
      if (!deepEqual(primary[field], alternative[field])) {
        changedFields.push(field);
      }
    }

    summaries.push({
      stepNumber,
      stepLabel: config.label,
      hasChanges: changedFields.length > 0,
      changedFields,
    });
  }

  return summaries;
}

/**
 * Human-readable field labels for diff display.
 */
export const FIELD_LABELS: Record<string, string> = {
  planName: 'Plan name',
  planType: 'Plan type',
  planAsOfDate: 'Plan start date',
  persons: 'People details',
  holdings: 'Investment holdings',
  additionalPensions: 'Additional pensions',
  includeProperty: 'Property inclusion',
  property: 'Property details',
  properties: 'Properties',
  supplementaryIncome: 'Supplementary income',
  spendingPhases: 'Spending phases',
  oneTimeExpenses: 'One-time expenses',
  recurringExpenses: 'Recurring expenses',
  personDeceasedAges: 'Life expectancy',
  survivorSpendingPct: 'Survivor spending',
  careScenarios: 'Care scenarios',
  hasDirectDescendants: 'Direct descendants',
  includePensionsInEstate: 'Pensions in estate',
  ihtStrategies: 'IHT strategies',
  householdCash: 'Household cash',
  economicAssumptions: 'Economic assumptions',
  decumulationStrategy: 'Withdrawal strategy',
  projectionYears: 'Projection years',
  isaAnnualAllowance: 'ISA allowance',
  isaShelteringMode: 'ISA sheltering mode',
  bandFillMode: 'SIPP band-fill mode',
  guardrailConfig: 'Guardrail configuration',
  timeHorizonRebalancing: 'Time-horizon rebalancing',
  spendingApproachConfig: 'Spending approach',
  emergencyFund: 'Emergency fund',
  careFund: 'Care fund',
  careFundReleaseAge: 'Care fund release age',
  annuityPurchases: 'Annuity purchase',
};

/**
 * Get human-readable change summary strings for the comparison strip.
 */
export function getChangedFieldSummary(
  primary: Record<string, unknown>,
  alternative: Record<string, unknown>
): string[] {
  const changes: string[] = [];
  const stepChanges = computeStepChanges(primary, alternative);

  for (const step of stepChanges) {
    for (const field of step.changedFields) {
      const label = FIELD_LABELS[field] || field;

      // For simple scalar fields, show old → new
      const pVal = primary[field];
      const aVal = alternative[field];
      if (typeof pVal !== 'object' && typeof aVal !== 'object') {
        changes.push(`${label}: ${isEmptyValue(pVal) ? '—' : String(pVal)} → ${isEmptyValue(aVal) ? '—' : String(aVal)}`);
      } else {
        changes.push(`${label} changed`);
      }
    }
  }

  return changes;
}

// ── Detailed step-grouped change descriptions ──────────────────────────────

export interface StepChangeDetail {
  stepNumber: number;
  stepLabel: string;
  descriptions: string[];
}

/** Safe accessor for nested object values */
function get(obj: unknown, path: string): unknown {
  let current = obj;
  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function fmtCurrency(v: unknown): string {
  if (typeof v === 'number') return formatCurrency(v, true);
  if (v == null) return '—';
  return typeof v === 'string' ? v : '—';
}

function fmtPct(v: unknown): string {
  if (typeof v === 'number') return `${v}%`;
  if (v == null) return '—';
  return typeof v === 'string' ? v : '—';
}

function fmtBool(v: unknown): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (v == null) return '—';
  return typeof v === 'string' ? v : '—';
}

/** Sum total holdings value across all persons. */
function sumHoldings(holdings: unknown): number {
  if (!holdings || typeof holdings !== 'object') return 0;
  let total = 0;
  for (const personHoldings of Object.values(holdings as Record<string, unknown>)) {
    if (!Array.isArray(personHoldings)) continue;
    for (const h of personHoldings) {
      if (h && typeof h === 'object' && 'currentValue' in h) {
        total += (h as { currentValue: number }).currentValue;
      }
    }
  }
  return total;
}

/** Count holdings across all persons. */
function countHoldings(holdings: unknown): number {
  if (!holdings || typeof holdings !== 'object') return 0;
  let count = 0;
  for (const personHoldings of Object.values(holdings as Record<string, unknown>)) {
    if (Array.isArray(personHoldings)) count += personHoldings.length;
  }
  return count;
}

/** Sum total spending per year across all phases. */
function sumSpendingPerYear(phases: unknown): number {
  if (!Array.isArray(phases)) return 0;
  // Just sum the first phase's annual total as representative
  const first = phases[0] as Record<string, unknown> | undefined;
  if (!first) return 0;
  return (Number(first.livingExpenses) || 0) +
         (Number(first.travelLeisure) || 0) +
         (Number(first.otherDiscretionary) || 0);
}

/** Describer function type: appends human-readable descriptions to `out`. */
type FieldDescriber = (pVal: unknown, aVal: unknown, out: string[]) => void;

function arrayLenDescriber(label: string, unit: string): FieldDescriber {
  return (pVal, aVal, out) => {
    out.push(`${label}: ${Array.isArray(pVal) ? pVal.length : 0} → ${Array.isArray(aVal) ? aVal.length : 0} ${unit}`);
  };
}

/** Map of field name → describer function for generating change descriptions. */
const FIELD_DESCRIBERS: Record<string, FieldDescriber> = {
  planName: (p, a, out) => out.push(`Plan name: ${String(p)} → ${String(a)}`),
  planType: (p, a, out) => out.push(`Plan type: ${p === 'JOINT' ? 'Joint' : 'Single'} → ${a === 'JOINT' ? 'Joint' : 'Single'}`),
  planAsOfDate: (p, a, out) => out.push(`Start date: ${String(p)} → ${String(a)}`),
  persons: describePersonChanges,
  holdings: describeHoldingChanges,
  additionalPensions: (_p, _a, out) => out.push('Additional pension details changed'),
  contributions: (_p, _a, out) => out.push('Pre-retirement contributions changed'),
  includeProperty: (p, a, out) => out.push(`Property: ${fmtBool(p)} → ${fmtBool(a)}`),
  property: describePropertyChanges,
  properties: describePropertiesArrayChanges,
  supplementaryIncome: describeIncomeChanges,
  spendingPhases: describeSpendingChanges,
  oneTimeExpenses: arrayLenDescriber('One-time expenses', 'items'),
  recurringExpenses: arrayLenDescriber('Recurring expenses', 'items'),
  personDeceasedAges: describeDeceasedAgeChanges,
  survivorSpendingPct: (p, a, out) => out.push(`Survivor spending: ${fmtPct(p)} → ${fmtPct(a)}`),
  careScenarios: arrayLenDescriber('Care scenarios', 'scenarios'),
  hasDirectDescendants: (p, a, out) => out.push(`Direct descendants: ${fmtBool(p)} → ${fmtBool(a)}`),
  includePensionsInEstate: (p, a, out) => out.push(`Pensions in estate: ${fmtBool(p)} → ${fmtBool(a)}`),
  ihtStrategies: (_p, _a, out) => out.push('IHT strategy settings changed'),
  householdCash: describeHouseholdCashChanges,
  economicAssumptions: describeEconomicAssumptionChanges,
  decumulationStrategy: (p, a, out) => out.push(`Withdrawal strategy: ${formatStrategy(p)} → ${formatStrategy(a)}`),
  projectionYears: (p, a, out) => out.push(`Projection length: ${p} → ${a} years`),
  isaAnnualAllowance: (p, a, out) => out.push(`ISA allowance: ${fmtCurrency(p)} → ${fmtCurrency(a)}`),
  isaShelteringMode: (p, a, out) => out.push(`ISA sheltering: ${formatSheltering(p)} → ${formatSheltering(a)}`),
  bandFillMode: (p, a, out) => out.push(`SIPP band-fill: ${formatBandFill(p)} → ${formatBandFill(a)}`),
  guardrailConfig: (_p, _a, out) => out.push('Guardrail settings changed'),
  timeHorizonRebalancing: (_p, _a, out) => out.push('Time-horizon rebalancing settings changed'),
  spendingApproachConfig: (_p, _a, out) => out.push('Spending approach settings changed'),
  emergencyFund: (p, a, out) => describeOptionalMoney('Emergency fund', p, a, out),
  careFund: (p, a, out) => describeOptionalMoney('Care fund', p, a, out),
  careFundReleaseAge: (p, a, out) => {
    // An explicit 0/null replacing an unset field is not a change worth words
    if ((p ?? null) === (a ?? null)) return;
    out.push(`Care fund release age: ${p ?? 'none'} → ${a ?? 'none'}`);
  },
  annuityPurchases: describeAnnuityChanges,
};

/** An optional money field: "none" when unset (undefined/null/0), else the amount. */
function fmtOptionalCurrency(v: unknown): string {
  return typeof v === 'number' && v > 0 ? fmtCurrency(v) : 'none';
}

/**
 * Say nothing when both sides mean "not set". An explicit 0 replacing an unset
 * field is the same plan, and "Emergency fund: none → none" is noise that
 * makes a reader hunt for a change that is not there.
 */
function describeOptionalMoney(label: string, previous: unknown, alternative: unknown, out: string[]): void {
  const before = fmtOptionalCurrency(previous);
  const after = fmtOptionalCurrency(alternative);
  if (before === after) return;
  out.push(`${label}: ${before} → ${after}`);
}

/**
 * Generate detailed, human-readable change descriptions grouped by step.
 * Produces rich before/after descriptions for complex fields instead of just "changed".
 */
export function getDetailedStepChanges(
  primary: Record<string, unknown>,
  alternative: Record<string, unknown>,
): StepChangeDetail[] {
  const stepChanges = computeStepChanges(primary, alternative);
  const details: StepChangeDetail[] = [];

  for (const step of stepChanges) {
    if (!step.hasChanges) continue;

    const descriptions: string[] = [];
    for (const field of step.changedFields) {
      const describer = FIELD_DESCRIBERS[field];
      if (describer) {
        describer(primary[field], alternative[field], descriptions);
      } else {
        descriptions.push(`${FIELD_LABELS[field] || field} changed`);
      }
    }

    if (descriptions.length > 0) {
      details.push({
        stepNumber: step.stepNumber,
        stepLabel: step.stepLabel,
        descriptions,
      });
    }
  }

  return details;
}

// ── Field-specific describers ──────────────────────────────────────────────

type PersonFieldDescriber = {
  key: string;
  describe: (name: string, p: Record<string, unknown>, a: Record<string, unknown>) => string;
};

const PERSON_FIELD_DESCRIBERS: PersonFieldDescriber[] = [
  { key: 'retirementDate', describe: (name, p, a) => `${name}: retirement date ${String(p.retirementDate)} → ${String(a.retirementDate)}` },
  { key: 'currentStatePensionAnnual', describe: (name, p, a) => `${name}: state pension ${fmtCurrency(p.currentStatePensionAnnual)} → ${fmtCurrency(a.currentStatePensionAnnual)}/yr` },
  { key: 'dateOfBirth', describe: (name) => `${name}: date of birth changed` },
  { key: 'taxFreeLumpSumPct', describe: (name, p, a) => `${name}: PCLS ${fmtPct(p.taxFreeLumpSumPct)} → ${fmtPct(a.taxFreeLumpSumPct)}` },
  { key: 'lsaUsedPrePlan', describe: (name, p, a) => `${name}: tax-free cash taken ${fmtCurrency(p.lsaUsedPrePlan ?? 0)} → ${fmtCurrency(a.lsaUsedPrePlan ?? 0)}` },
  { key: 'priorOtherTaxableIncome', describe: (name, p, a) => `${name}: non-dividend income already received this tax year ${fmtCurrency(p.priorOtherTaxableIncome ?? 0)} → ${fmtCurrency(a.priorOtherTaxableIncome ?? 0)}` },
  { key: 'priorDividendIncome', describe: (name, p, a) => `${name}: dividend income already received this tax year ${fmtCurrency(p.priorDividendIncome ?? 0)} → ${fmtCurrency(a.priorDividendIncome ?? 0)}` },
  { key: 'priorPensionContributions', describe: (name, p, a) => `${name}: pension contributions already made this tax year ${fmtCurrency(p.priorPensionContributions ?? 0)} → ${fmtCurrency(a.priorPensionContributions ?? 0)}` },
];

/** Narrows an `unknown` plan-data field to a number, or undefined if it isn't one. */
function numOrUndef(v: unknown): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

function describePersonDiff(name: string, p: Record<string, unknown>, a: Record<string, unknown>, out: string[]): void {
  const beforeLen = out.length;
  // priorOtherTaxableIncome is derived, not persisted, once a plan has ever
  // been through Step 2 since the salary/drawdown/State Pension/rental split
  // (phase 3, derived-total refactor) — reading it straight off the raw
  // formData like every other field here would silently compare `undefined`
  // against `undefined` and report no change even when the breakdown (the
  // actual source of truth) genuinely differs between the two plans. Derive
  // it the same way every other read site does (deriveTotalPriorOtherIncome)
  // and diff/describe the derived figure instead of the raw field.
  const pTotal = deriveTotalPriorOtherIncome(
    numOrUndef(p.priorSalaryIncome), numOrUndef(p.priorDrawdownIncome), numOrUndef(p.priorStatePensionIncome),
    numOrUndef(p.priorRentalIncome), numOrUndef(p.priorOtherTaxableIncome)
  );
  const aTotal = deriveTotalPriorOtherIncome(
    numOrUndef(a.priorSalaryIncome), numOrUndef(a.priorDrawdownIncome), numOrUndef(a.priorStatePensionIncome),
    numOrUndef(a.priorRentalIncome), numOrUndef(a.priorOtherTaxableIncome)
  );
  const pWithTotal = { ...p, priorOtherTaxableIncome: pTotal };
  const aWithTotal = { ...a, priorOtherTaxableIncome: aTotal };
  for (const fd of PERSON_FIELD_DESCRIBERS) {
    const pVal = fd.key === 'priorOtherTaxableIncome' ? pTotal : p[fd.key];
    const aVal = fd.key === 'priorOtherTaxableIncome' ? aTotal : a[fd.key];
    if (fieldsDiffer(pVal, aVal)) {
      // Treat undefined and 0 as equivalent for optional numeric fields (backwards compat)
      // `?? 0` alone doesn't catch NaN (react-hook-form's empty-numeric-input
      // value) — without isEmptyValue, a cleared field compared against an
      // unset/zero counterpart on the other plan would slip past this
      // suppression and still emit a spurious diff line below.
      if (
        (fd.key === 'lsaUsedPrePlan' || fd.key === 'priorOtherTaxableIncome' || fd.key === 'priorDividendIncome' || fd.key === 'priorPensionContributions')
        && (isEmptyValue(pVal) ? 0 : pVal) === (isEmptyValue(aVal) ? 0 : aVal)
      ) continue;
      out.push(fd.describe(name, pWithTotal, aWithTotal));
    }
  }
  // priorOtherTaxableIncome (and the four breakdown fields behind it) must
  // be excluded here too, not just from the per-field describers above: the
  // migration from a bare total to the breakdown shape (or vice versa)
  // changes which raw keys exist on `p` vs `a` even when the DERIVED total
  // (pTotal/aTotal, already compared above) is unchanged — e.g. a legacy
  // plan with only `priorOtherTaxableIncome: 12000` next to the same plan
  // re-saved through Step 2, which now carries priorSalaryIncome/
  // priorDrawdownIncome/priorStatePensionIncome/priorRentalIncome instead and
  // no longer persists the total at all. None of those five raw fields has
  // its own describer — their only real comparison is the derived total
  // above — so comparing them again here as raw keys only re-litigates a
  // question already answered, and does so incorrectly: the shape change
  // alone used to trip `!deepEqual`, producing a phantom "details changed"
  // line with nothing actually named (LOW, #1592 phase 2 review round 4).
  const {
    priorOtherTaxableIncome: _pRawTotal, priorSalaryIncome: _pSalary, priorDrawdownIncome: _pDrawdown,
    priorStatePensionIncome: _pStatePension, priorRentalIncome: _pRental,
    ...pRest
  } = p;
  const {
    priorOtherTaxableIncome: _aRawTotal, priorSalaryIncome: _aSalary, priorDrawdownIncome: _aDrawdown,
    priorStatePensionIncome: _aStatePension, priorRentalIncome: _aRental,
    ...aRest
  } = a;
  if (out.length === beforeLen && !deepEqual(pRest, aRest)) {
    out.push(`${name}: details changed`);
  }
}

function describePersonChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  if (!Array.isArray(pVal) || !Array.isArray(aVal)) {
    out.push('People details changed');
    return;
  }
  const maxLen = Math.max(pVal.length, aVal.length);
  for (let i = 0; i < maxLen; i++) {
    const p = pVal[i] as Record<string, unknown> | undefined;
    const a = aVal[i] as Record<string, unknown> | undefined;
    if (!p && a) { out.push(`Added person: ${a.fullName}`); continue; }
    if (p && !a) { out.push(`Removed person: ${p.fullName}`); continue; }
    if (!p || !a) continue;
    describePersonDiff(String(a.fullName || p.fullName), p, a, out);
  }
}

function describeHoldingChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const pCount = countHoldings(pVal);
  const aCount = countHoldings(aVal);
  const pTotal = sumHoldings(pVal);
  const aTotal = sumHoldings(aVal);

  if (pCount !== aCount) {
    out.push(`Holdings: ${pCount} → ${aCount} holdings`);
  }
  if (Math.abs(pTotal - aTotal) > 0.01) {
    out.push(`Total invested: ${fmtCurrency(pTotal)} → ${fmtCurrency(aTotal)}`);
  }
  if (pCount === aCount && Math.abs(pTotal - aTotal) < 0.01) {
    out.push('Investment allocation changed');
  }
}

function describePropertyChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const p = pVal as Record<string, unknown> | null;
  const a = aVal as Record<string, unknown> | null;
  if (!p && !a) return;
  if (!p || !a) { out.push('Property configuration changed'); return; }

  const beforeLen = out.length;
  if (fieldsDiffer(p.currentMarketValue, a.currentMarketValue)) {
    out.push(`Market value: ${fmtCurrency(p.currentMarketValue)} → ${fmtCurrency(a.currentMarketValue)}`);
  }
  if (fieldsDiffer(p.currentMortgageAmount, a.currentMortgageAmount)) {
    out.push(`Mortgage: ${fmtCurrency(p.currentMortgageAmount)} → ${fmtCurrency(a.currentMortgageAmount)}`);
  }
  if (fieldsDiffer(p.planningSale, a.planningSale)) {
    out.push(`Planning sale: ${fmtBool(p.planningSale)} → ${fmtBool(a.planningSale)}`);
  }
  if (fieldsDiffer(p.plannedSaleDate, a.plannedSaleDate)) {
    out.push(`Sale date: ${typeof p.plannedSaleDate === 'string' ? p.plannedSaleDate : '—'} → ${typeof a.plannedSaleDate === 'string' ? a.plannedSaleDate : '—'}`);
  }
  if (fieldsDiffer(p.newPropertyPurchasePrice, a.newPropertyPurchasePrice)) {
    out.push(`New property purchase: ${fmtCurrency(p.newPropertyPurchasePrice)} → ${fmtCurrency(a.newPropertyPurchasePrice)}`);
  }
  if (fieldsDiffer(p.proceedsStrategy, a.proceedsStrategy)) {
    out.push(`Proceeds strategy: ${formatProceeds(p.proceedsStrategy)} → ${formatProceeds(a.proceedsStrategy)}`);
  }
  if (fieldsDiffer(p.monthlyMortgagePayment, a.monthlyMortgagePayment)) {
    out.push(`Monthly mortgage: ${fmtCurrency(p.monthlyMortgagePayment)} → ${fmtCurrency(a.monthlyMortgagePayment)}`);
  }
  if (out.length === beforeLen) {
    out.push('Property details changed');
  }
}

function describePropertiesArrayChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const pArr: unknown[] = Array.isArray(pVal) ? pVal : [];
  const aArr: unknown[] = Array.isArray(aVal) ? aVal : [];
  if (pArr.length !== aArr.length) {
    out.push(`Properties: ${pArr.length} → ${aArr.length} properties`);
  }
  const pTotal = sumNumericField(pArr, 'currentMarketValue');
  const aTotal = sumNumericField(aArr, 'currentMarketValue');
  if (Math.abs(pTotal - aTotal) > 0.01) {
    out.push(`Total property value: ${fmtCurrency(pTotal)} → ${fmtCurrency(aTotal)}`);
  }
  if (pArr.length === aArr.length && Math.abs(pTotal - aTotal) < 0.01) {
    out.push('Property details changed');
  }
}

function describeIncomeChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const pArr: unknown[] = Array.isArray(pVal) ? pVal : [];
  const aArr: unknown[] = Array.isArray(aVal) ? aVal : [];
  const beforeLen = out.length;

  if (pArr.length !== aArr.length) {
    out.push(`Income sources: ${pArr.length} → ${aArr.length} items`);
  }

  const pTotal = sumNumericField(pArr, 'grossAnnualAmount');
  const aTotal = sumNumericField(aArr, 'grossAnnualAmount');

  if (Math.abs(pTotal - aTotal) > 0.01) {
    out.push(`Total supplementary income: ${fmtCurrency(pTotal)} → ${fmtCurrency(aTotal)}/yr`);
  }

  if (out.length === beforeLen) {
    out.push('Income timing or details changed');
  }
}

function describeSpendingChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const beforeLen = out.length;
  const pTotal = sumSpendingPerYear(pVal);
  const aTotal = sumSpendingPerYear(aVal);

  if (Math.abs(pTotal - aTotal) > 0.01) {
    out.push(`Phase 1 annual spending: ${fmtCurrency(pTotal)} → ${fmtCurrency(aTotal)}`);
  }

  if (Array.isArray(pVal) && Array.isArray(aVal)) {
    for (let i = 0; i < Math.max(pVal.length, aVal.length); i++) {
      const pp = pVal[i] as Record<string, unknown> | undefined;
      const ap = aVal[i] as Record<string, unknown> | undefined;
      if (!pp || !ap) continue;
      if (fieldsDiffer(pp.startYear, ap.startYear) || fieldsDiffer(pp.endYear, ap.endYear)) {
        out.push(`Phase ${i + 1} "${ap.label || pp.label}": years ${pp.startYear}-${pp.endYear} → ${ap.startYear}-${ap.endYear}`);
      }
    }
  }

  if (out.length === beforeLen) {
    out.push('Spending phase details changed');
  }
}

function describeDeceasedAgeChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  if (!Array.isArray(pVal) || !Array.isArray(aVal)) {
    out.push('Life expectancy changed');
    return;
  }
  for (let i = 0; i < Math.max(pVal.length, aVal.length); i++) {
    const p = pVal[i] as { deceasedAge: number | null } | undefined;
    const a = aVal[i] as { deceasedAge: number | null } | undefined;
    if (fieldsDiffer(p?.deceasedAge, a?.deceasedAge)) {
      out.push(`Person ${i + 1} life expectancy: age ${p?.deceasedAge ?? '—'} → ${a?.deceasedAge ?? '—'}`);
    }
  }
}

function describeHouseholdCashChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const beforeLen = out.length;
  const pCash = get(pVal, 'jointAccountBalance');
  const aCash = get(aVal, 'jointAccountBalance');
  if (fieldsDiffer(pCash, aCash)) {
    out.push(`Cash balance: ${fmtCurrency(pCash)} → ${fmtCurrency(aCash)}`);
  }
  if (out.length === beforeLen) {
    out.push('Household cash settings changed');
  }
}

function describeEconomicAssumptionChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const p = pVal as Record<string, unknown> | null;
  const a = aVal as Record<string, unknown> | null;
  if (!p || !a) { out.push('Economic assumptions changed'); return; }

  const beforeLen = out.length;
  if (fieldsDiffer(p.equityReturnAnnualPct, a.equityReturnAnnualPct)) {
    out.push(`Equity return: ${fmtPct(p.equityReturnAnnualPct)} → ${fmtPct(a.equityReturnAnnualPct)}`);
  }
  if (fieldsDiffer(p.bondReturnAnnualPct, a.bondReturnAnnualPct)) {
    out.push(`Bond return: ${fmtPct(p.bondReturnAnnualPct)} → ${fmtPct(a.bondReturnAnnualPct)}`);
  }
  if (fieldsDiffer(p.inflationRateAnnualPct, a.inflationRateAnnualPct)) {
    out.push(`Inflation: ${fmtPct(p.inflationRateAnnualPct)} → ${fmtPct(a.inflationRateAnnualPct)}`);
  }
  if (out.length === beforeLen) {
    out.push('Economic assumptions changed');
  }
}

// ── Enum formatters ────────────────────────────────────────────────────────

function formatStrategy(v: unknown): string {
  const map: Record<string, string> = {
    TAX_OPTIMISED: 'Tax-optimised',
    CASH: 'Cash-first',
    PROPORTIONAL: 'Proportional',
  };
  return map[String(v)] ?? String(v);
}

function formatSheltering(v: unknown): string {
  const map: Record<string, string> = {
    FULL: 'Full',
    NONE: 'None',
    GAINS_ONLY: 'Gains only',
  };
  return map[String(v)] ?? String(v);
}

function formatBandFill(v: unknown): string {
  const map: Record<string, string> = {
    PA_AND_BASIC: 'PA + Basic rate',
    PA_ONLY: 'PA only',
  };
  return map[String(v)] ?? String(v);
}

function formatProceeds(v: unknown): string {
  const map: Record<string, string> = {
    INVEST: 'Invest',
    CASH: 'Cash',
  };
  return map[String(v)] ?? String(v);
}

/**
 * Describe annuity purchase changes per person. The wizard stores at most one
 * purchase per person, keyed person0/person1, so this reports added, removed
 * and changed purchases rather than a bare "changed".
 */
function describeAnnuityChanges(pVal: unknown, aVal: unknown, out: string[]): void {
  const primary = (pVal ?? {}) as Record<string, unknown[]>;
  const alternative = (aVal ?? {}) as Record<string, unknown[]>;
  const keys = new Set([...Object.keys(primary), ...Object.keys(alternative)]);

  for (const key of [...keys].sort((a, b) => a.localeCompare(b))) {
    const before = Array.isArray(primary[key]) ? primary[key][0] : undefined;
    const after = Array.isArray(alternative[key]) ? alternative[key][0] : undefined;
    if (deepEqual(before, after)) continue;

    const label = keys.size > 1 ? `${key === 'person0' ? 'Person 1' : 'Person 2'}: annuity` : 'Annuity';
    if (!before && after) {
      const age = String((after as Record<string, unknown>).purchaseAge);
      out.push(`${label} added (age ${age})`);
    } else if (before && !after) {
      out.push(`${label} removed`);
    } else if (before && after) {
      const b = before as Record<string, unknown>;
      const a = after as Record<string, unknown>;
      if (b.purchaseAge !== a.purchaseAge) {
        out.push(`${label} purchase age: ${String(b.purchaseAge)} → ${String(a.purchaseAge)}`);
      } else {
        out.push(`${label} settings changed`);
      }
    }
  }
}
